import type { BridgeId, VesselCorridor } from "./contracts";

export type GeographicPoint = readonly [latitude: number, longitude: number];

/** Geographic centerlines are shared with the Worker projection layer. */
export const GEOGRAPHIC_ROUTES = {
  miami_river: [
    [25.771, -80.1849],
    [25.7699, -80.19005],
    [25.7692, -80.1938],
    [25.768907, -80.197552],
    [25.773038, -80.200591],
    [25.774205, -80.201287],
    [25.778307, -80.206931],
    [25.782594, -80.214716],
    [25.785884, -80.222961],
    [25.788202, -80.231373],
    [25.79267, -80.23965],
    [25.8085, -80.255],
  ],
  north_approach: [
    [25.771, -80.1849],
    [25.7748, -80.1832],
    [25.7779, -80.1799],
    [25.7793, -80.1665],
  ],
  government_cut: [
    [25.771, -80.1849],
    [25.7755, -80.184],
    [25.7726, -80.1822],
    [25.7723, -80.175],
    [25.7682, -80.167],
    [25.7663, -80.1615],
    [25.766, -80.16],
    [25.769, -80.15],
    [25.7672, -80.146],
    [25.7666, -80.143],
    [25.7653, -80.14],
    [25.7637, -80.134],
    [25.7622, -80.129],
  ],
  south_approach: [
    [25.771, -80.1849],
    [25.769, -80.1824],
    [25.7663, -80.183],
    [25.762, -80.1845],
    [25.752, -80.181],
    [25.746, -80.17],
  ],
} as const satisfies Record<VesselCorridor, readonly (readonly [number, number])[]>;

/** Fixed geodesic arc lengths; route measures must not vary with the queried latitude. */
export const GEOGRAPHIC_ROUTE_ARCS = {
  miami_river: routeArcMeters(GEOGRAPHIC_ROUTES.miami_river),
  north_approach: routeArcMeters(GEOGRAPHIC_ROUTES.north_approach),
  government_cut: routeArcMeters(GEOGRAPHIC_ROUTES.government_cut),
  south_approach: routeArcMeters(GEOGRAPHIC_ROUTES.south_approach),
} satisfies Record<VesselCorridor, readonly number[]>;

function routeArcMeters(points: readonly GeographicPoint[]): readonly number[] {
  let arcMeters = 0;
  return points.map((point, index) => {
    if (index > 0) arcMeters += geographicDistanceMeters(points[index - 1]!, point);
    return arcMeters;
  });
}

export const BRIDGE_GEOGRAPHIC_POINTS = {
  brickell: GEOGRAPHIC_ROUTES.miami_river[1],
  "sw-2-ave": GEOGRAPHIC_ROUTES.miami_river[3],
  "sw-1-st": GEOGRAPHIC_ROUTES.miami_river[4],
  "w-flagler": GEOGRAPHIC_ROUTES.miami_river[5],
  "nw-5-st": GEOGRAPHIC_ROUTES.miami_river[6],
  "nw-12-ave": GEOGRAPHIC_ROUTES.miami_river[7],
  "nw-17-ave": GEOGRAPHIC_ROUTES.miami_river[8],
  "nw-22-ave": GEOGRAPHIC_ROUTES.miami_river[9],
  "nw-27-ave": GEOGRAPHIC_ROUTES.miami_river[10],
} as const satisfies Record<BridgeId, GeographicPoint>;

export const RIVER_BRICKELL_GEOGRAPHIC_POINT = BRIDGE_GEOGRAPHIC_POINTS.brickell;
export const RIVER_MOUTH_S_METERS = -GEOGRAPHIC_ROUTE_ARCS.miami_river[1]!;
export const RIVER_OUTER_S_METERS = GEOGRAPHIC_ROUTE_ARCS.miami_river.at(-1)! + RIVER_MOUTH_S_METERS;
export const BRIDGE_S_METERS = Object.fromEntries(
  Object.entries(BRIDGE_GEOGRAPHIC_POINTS).map(([id, point]) => [
    id,
    GEOGRAPHIC_ROUTE_ARCS.miami_river[GEOGRAPHIC_ROUTES.miami_river.indexOf(point)]! + RIVER_MOUTH_S_METERS,
  ]),
) as Record<BridgeId, number>;

export function geographicDistanceMeters(a: GeographicPoint, b: GeographicPoint): number {
  const radians = Math.PI / 180;
  const deltaLatitude = (b[0] - a[0]) * radians;
  const deltaLongitude = (b[1] - a[1]) * radians;
  const value =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(a[0] * radians) * Math.cos(b[0] * radians) *
      Math.sin(deltaLongitude / 2) ** 2;
  return 6_371_000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}
